import React, { useEffect, useState, Children } from 'react';
import { LayoutDashboardIcon, ServerIcon, ShieldIcon, UsersIcon, KeyIcon, MessageSquareIcon, CpuIcon, BellIcon, UserIcon, LogOutIcon, AppWindowIcon, GitBranchIcon, ActivityIcon, MessageCircleIcon, DollarSignIcon } from 'lucide-react';
interface AuthUser {
  username: string;
  name: string;
  role: string;
}
interface DashboardLayoutProps {
  children: React.ReactNode;
  currentPage: string;
  setCurrentPage: (page: string) => void;
  user: AuthUser;
  onLogout: () => void;
}
export function DashboardLayout({
  children,
  currentPage,
  setCurrentPage,
  user,
  onLogout
}: DashboardLayoutProps) {
  const [prevPage, setPrevPage] = useState(currentPage);
  const [contentVisible, setContentVisible] = useState(true);
  const [displayedChildren, setDisplayedChildren] = useState(children);
  // Handle page transitions
  useEffect(() => {
    if (currentPage !== prevPage) {
      // Start transition - hide current content
      setContentVisible(false);
      // After content is hidden, update the content and previous page reference
      const updateTimer = setTimeout(() => {
        setDisplayedChildren(children);
        setPrevPage(currentPage);
        // Show new content with fade-in after a brief delay
        setTimeout(() => {
          setContentVisible(true);
        }, 100); // Short delay before fade-in starts
      }, 300); // Wait for fade-out to complete
      return () => {
        clearTimeout(updateTimer);
      };
    } else {
      // If it's the initial render or same page, just display the content
      setDisplayedChildren(children);
    }
  }, [currentPage, prevPage, children]);
  const navItems = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: <LayoutDashboardIcon size={20} data-id="element-75" />
  }, {
    id: 'realtime',
    label: 'Realtime',
    icon: <ActivityIcon size={20} data-id="element-76" />
  }, {
    id: 'chat',
    label: 'Chat Console',
    icon: <MessageCircleIcon size={20} data-id="element-77" />
  }, {
    id: 'models',
    label: 'Model Management',
    icon: <ServerIcon size={20} data-id="element-78" />
  }, {
    id: 'costs',
    label: 'Cost Intelligence',
    icon: <DollarSignIcon size={20} data-id="element-79" />
  }, {
    id: 'guardrails',
    label: 'Guardrails',
    icon: <ShieldIcon size={20} data-id="element-80" />
  }, {
    id: 'access',
    label: 'Access Control',
    icon: <UsersIcon size={20} data-id="element-81" />
  }, {
    id: 'prompts',
    label: 'Observability',
    icon: <MessageSquareIcon size={20} data-id="element-82" />
  }, {
    id: 'gpu',
    label: 'GPU Overview',
    icon: <CpuIcon size={20} data-id="element-83" />
  }, {
    id: 'marketplace',
    label: 'App Marketplace',
    icon: <AppWindowIcon size={20} data-id="element-84" />
  }, {
    id: 'workflow',
    label: 'Workflow Builder',
    icon: <GitBranchIcon size={20} data-id="element-85" />
  }];
  return <div className="flex h-screen w-full bg-black" data-id="element-86">
      {/* Sidebar */}
      <div className="w-64 bg-[#0A0A0A] border-r border-[#222222] flex flex-col shadow-lg transition-all duration-300" data-id="element-87">
        <div className="px-4 py-4 border-b border-[#222222] flex items-center" data-id="element-88">
          <div className="flex flex-col" data-id="element-89">
            <div className="flex items-center" data-id="element-90">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vseaUET8yPx3mvKjBCRrif/Ghisha_Logo_0-_black.001-Photoroom.png" alt="Ghisha Logo" className="h-16 mr-2" data-id="element-91" />
            </div>
            <p className="text-xs text-gray-400 mt-1" data-id="element-92">
              AI Lifecycle Management Platform
            </p>
          </div>
        </div>
        <nav className="flex-1 pt-4" data-id="element-93">
          <ul data-id="element-94">
            {navItems.map(item => <li key={item.id} className="transition-all duration-200" data-id="element-95">
                <button onClick={() => setCurrentPage(item.id)} className={`flex items-center w-full px-4 py-2 text-sm transition-all duration-200 ${currentPage === item.id ? 'bg-gradient-to-r from-[#121212] to-[#0F0F0F] text-white border-r-4 border-white' : 'text-gray-400 hover:bg-[#121212] hover:text-white'}`} data-id="element-96">
                  <span className="mr-3 transition-transform duration-300 transform group-hover:scale-110" data-id="element-97">
                    {item.icon}
                  </span>
                  {item.label}
                </button>
              </li>)}
          </ul>
        </nav>
        <div className="p-4 border-t border-[#222222]" data-id="element-98">
          <div className="flex items-center justify-between" data-id="element-99">
            <div className="flex items-center" data-id="element-100">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white transition-transform hover:scale-105 duration-200" data-id="element-101">
                <UserIcon size={16} data-id="element-102" />
              </div>
              <div className="ml-3" data-id="element-103">
                <p className="text-sm font-medium text-white" data-id="element-104">{user.name}</p>
                <p className="text-xs text-gray-400 capitalize" data-id="element-105">{user.role}</p>
              </div>
            </div>
            <button className="text-gray-400 hover:text-white transition-colors duration-200" onClick={onLogout} data-id="element-106">
              <LogOutIcon size={18} data-id="element-107" />
            </button>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-108">
        {/* Header */}
        <header className="bg-[#0A0A0A] border-b border-[#222222] flex items-center justify-between p-4 shadow-md" data-id="element-109">
          <h2 className="text-xl font-semibold text-white" data-id="element-110">
            {navItems.find(item => item.id === currentPage)?.label || 'Dashboard'}
          </h2>
          <div className="flex items-center" data-id="element-111">
            <button className="p-2 rounded-full text-gray-400 hover:text-white hover:bg-[#121212] relative transition-all duration-200 hover:scale-105" data-id="element-112">
              <BellIcon size={20} data-id="element-113" />
              <span className="absolute top-1 right-1 h-2 w-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full animate-pulse" data-id="element-114"></span>
            </button>
          </div>
        </header>
        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6 bg-black relative" data-id="element-115">
          <div className={`transition-opacity duration-300 ease-in-out ${contentVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'}`} data-id="element-116">
            {displayedChildren}
          </div>
        </main>
      </div>
    </div>;
}